import { createContext, type PropsWithChildren, use, useMemo } from 'react';
import { useMatches } from '@tanstack/react-router';
import { type ApiSourceBundle, useApiDefinitions } from '@interxfi/api-client/registry/api-source';

export enum DocsViewContext {
  Index = 'index',
  ApiReference = 'api-reference',
  Guides = 'guides',
}

export interface IDocsContext {
  bundle: ApiSourceBundle;
  version: string;
  effectiveVersion: string | undefined;
  viewContext: DocsViewContext;
}

export const DocsContext = createContext<IDocsContext>({
  bundle: 'program-api',
  version: 'latest',
  viewContext: DocsViewContext.Index,
  effectiveVersion: undefined,
});

export function DocsContextProvider({ bundle, children, version }: PropsWithChildren<Pick<IDocsContext, 'bundle' | 'version'>>) {
  const { data } = useApiDefinitions(version, bundle);
  const effectiveVersion = data?.metadata?.version;
  const matches = useMatches();
  const matchingViewContext = useMemo(() => {
    for (let i = matches.length - 1; i >= 0; i -= 1) {
      const match = matches[i];

      if (match.staticData && 'docsViewContext' in match.staticData) {
        return match.staticData.docsViewContext as DocsViewContext;
      }
    }

    return DocsViewContext.Index;
  }, [matches]);

  const value = useMemo(
    () => ({
      bundle,
      version,
      effectiveVersion,
      viewContext: matchingViewContext,
    }),
    [bundle, version, effectiveVersion, matchingViewContext],
  );

  return <DocsContext.Provider value={value}>{children}</DocsContext.Provider>;
}

export function useDocsContext() {
  const context = use(DocsContext);
  if (!context) {
    throw new Error('useDocsContext must be used within a DocsContextProvider');
  }

  return context;
}
